import { ColDef } from "ag-grid-community";
import { AgGrid } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { RiskRating } from "~/graphql/supergraphEnums";
import ProgramRenderer from "~/pages/Compliance/CompliancePositionsTable/ProgramRenderer";
import { RISK_VALUES } from "~/pages/Compliance/riskRating/constants";
import { COLUMN_NAMES, FIELD_NAMES, TOOLTIP_TEXT } from "./constants";
import { PositionData } from "./types";

// Enum for row types in order of sort priority (lower number = higher priority)
enum RowType {
  NORMAL = 1,
  NO_NAME = 2,
  NO_PROFILE = 3,
  NOT_APPLICABLE = 4,
  GROUP = 5,
}

const getPaymentBeneficiaryName = (profile: PositionData["linkedProfile"]): string => {
  if (!profile) return "";

  if (profile.legalName) {
    return profile.legalName;
  }

  if (profile.firstName && profile.lastName) {
    return `${profile.firstName} ${profile.lastName}`;
  }

  return "";
};

const isNotAttached = (data: PositionData): boolean => {
  if (!data.linkedOnboardingCase) return true;

  const hasNoProfiles =
    !data.linkedOnboardingCase.profiles || data.linkedOnboardingCase.profiles.length === 0;
  const hasNoInvestingProfile = !data.linkedOnboardingCase.investingProfile;

  return hasNoProfiles && hasNoInvestingProfile;
};

const getRowType = (data: PositionData): RowType => {
  if (Array.isArray(data.successorCases) && data.successorCases.length > 0) {
    return RowType.GROUP;
  }

  if (data.parentId !== undefined || isNotAttached(data)) {
    return RowType.NOT_APPLICABLE;
  }

  if (!data.linkedProfile) {
    return RowType.NO_PROFILE;
  }

  const name = getPaymentBeneficiaryName(data.linkedProfile);
  if (!name) {
    return RowType.NO_NAME;
  }

  return RowType.NORMAL;
};

export const getColumnDefs = (picrRevampEnabled: boolean): ColDef<PositionData>[] => [
  {
    headerName: COLUMN_NAMES.POSITION,
    field: FIELD_NAMES.POSITION,
    cellRenderer: "positionCellRenderer",
    minWidth: 250,
    flex: 1,
  },
  {
    headerName: COLUMN_NAMES.PROGRAM,
    field: FIELD_NAMES.PROGRAM,
    cellRenderer: ProgramRenderer,
    cellRendererParams: (params: AgGrid.ICellRendererParams<PositionData>) => ({
      value: [params.data?.program],
    }),
    // to override default cellStyle
    cellStyle: { padding: Sizes.SM },
    minWidth: 250,
    flex: 1,
    sortable: true,
    hide: !picrRevampEnabled,
  },
  {
    headerName: COLUMN_NAMES.PAYMENT_BENEFICIARY,
    field: FIELD_NAMES.PAYMENT_BENEFICIARY,
    cellRenderer: "paymentBeneficiaryCellRenderer",
    minWidth: 250,
    flex: 1,
    sortable: true,
    hide: picrRevampEnabled,
    valueGetter: (params) => {
      const data = params.data;
      if (!data) return { type: RowType.NO_NAME, name: "" };

      const type = getRowType(data);
      const name = type === RowType.NORMAL ? getPaymentBeneficiaryName(data.linkedProfile) : "";

      return { type, name };
    },
    comparator: (valueA, valueB) => {
      if (valueA.type !== valueB.type) {
        return valueA.type - valueB.type;
      }

      if (valueA.type === RowType.NORMAL) {
        return valueA.name.localeCompare(valueB.name);
      }

      return 0;
    },
  },
  {
    headerName: COLUMN_NAMES.SCREENING,
    hide: picrRevampEnabled,
    valueGetter: (params) => {
      const screeningRecord = params.data?.linkedProfile?.screeningRecord;
      return {
        status: screeningRecord?.status,
        result: screeningRecord?.result,
        sortValue: `${screeningRecord?.status || ""}_${screeningRecord?.result || ""}`,
      };
    },
    cellRenderer: "screeningCellRenderer",
    minWidth: 250,
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB) => {
      return valueA.sortValue.localeCompare(valueB.sortValue);
    },
    tooltipComponent: "screeningTooltipRenderer",
    headerComponent: "headerWithInfoComponent",
    headerComponentParams: {
      displayName: COLUMN_NAMES.SCREENING,
      tooltipContent: TOOLTIP_TEXT.SCREENING,
      enableSorting: true,
    },
  },
  {
    headerName: COLUMN_NAMES.UPDATED_RISK_RATING,
    valueGetter: (params) => {
      const currentRating = params.data?.simulatedRiskRating?.investorProfileRiskRating;
      const newRating =
        params.data?.linkedProfile?.riskRating?.overriddenRating ||
        params.data?.linkedProfile?.riskRating?.calculatedRating ||
        RiskRating.None;
      return {
        currentRiskRating: currentRating,
        newRiskRating: newRating,
        sortValue: RISK_VALUES[newRating],
      };
    },
    cellRenderer: "riskRatingCellRenderer",
    cellRendererParams: {
      picrRevampEnabled,
    },
    minWidth: 250,
    sortable: true,
    comparator: (valueA, valueB) => {
      return valueA.sortValue - valueB.sortValue;
    },
    tooltipComponent: "riskRatingTooltipRenderer",
    headerComponent: "headerWithInfoComponent",
    headerComponentParams: {
      displayName: COLUMN_NAMES.UPDATED_RISK_RATING,
      tooltipContent: TOOLTIP_TEXT.RISK_RATING,
      enableSorting: true,
    },
  },
  {
    headerName: COLUMN_NAMES.CURRENT_PAYMENT_INSTRUCTIONS,
    cellRenderer: "paymentInstructionCellRenderer",
    minWidth: 250,
    flex: 1,
    sortable: true,
    valueGetter: (params) => {
      const bankName = params.data?.currentBankName || "";
      const accountNumber = params.data?.redactedBankAccountNumber || "";
      return `${bankName} ${accountNumber}`.trim();
    },
  },
];
